// see: https://github.com/udecode/plate/blob/main/examples/src/typescript/plateTypes.ts

import type {
  Decorate,
  DecorateEntry,
  DOMHandler,
  EDescendant,
  EElement,
  EElementEntry,
  EElementOrText,
  ENode,
  ENodeEntry,
  EText,
  ETextEntry,
  InjectComponent,
  InjectProps,
  KeyboardHandler,
  OnChange,
  OverrideByKey,
  PlateEditor,
  PlatePlugin,
  PlatePluginInsertData,
  PlatePluginProps,
  PlateProps,
  PluginOptions,
  SerializeHtml,
  TElement,
  TNodeEntry,
  TReactEditor,
  TText,
  WithOverride,
} from '@udecode/plate-common';

import type { Providers } from './plugins/createVideoEmbedPlugin';

/*
Types:
  - General type improvements to all plate packages.
  - `Value = TElement[]`: Default value of an editor.
  - `TNode = TEditor<Value> | TElement | TText`
  - `TElement`: Note that `type: string` is included as it's the standard in Plate.
  - `TText`: it now accepts unknown props.
  - `TDescendant = TElement | TText`
  - `TAncestor = TEditor<Value> | TElement`
  - `ENode<V extends Value>`: Node of an editor value
  - `EElement<V extends Value>`: Element of an editor value
  - `EText<V extends Value>`: Text of an editor value
  - `EDescendant<V extends Value>`: Descendant of an editor value
  - `EAncestor<V extends Value>`: Ancestor of an editor value
  - `NodeOf<N extends TNode>`: A utility type to get all the node types from a root node type.
  - `ElementOf<N extends TNode>`: A utility type to get all the element nodes type from a root node.
  - `TextOf<N extends TNode>`: A utility type to get all the text node types from a root node type.
  - `DescendantOf<N extends TNode>`: A utility type to get all the descendant node types from a root node type.
  - `ChildOf<N extends TNode, I extends number = number>`: A utility type to get the child node types from a root node type.
  - `AncestorOf<N extends TNode>`: A utility type to get all the ancestor node types from a root node type.
  - `ValueOf<E extends TEditor<Value>>`: A helper type for getting the value of an editor.
  - `MarksOf<N extends TNode>`: A utility type to get all the mark types from a root node type.
  - `EMarks<V extends Value>`
  - `TNodeProps<N extends TNode>`: Convenience type for returning the props of a node.
  - `TNodeEntry<N extends TNode = TNode>`
  - `ENodeEntry<V extends Value>`: Node entry from an editor.
  - `TElementEntry<N extends TNode = TNode>`: Element entry from a node.
  - `TTextEntry<N extends TNode = TNode>`: Text node entry from a node.
  - `ETextEntry<V extends Value>`: Text node entry of a value.
  - `TAncestorEntry<N extends TNode = TNode>`: Ancestor entry from a node.
  - `EAncestorEntry<V extends Value>`: Ancestor entry from an editor.
  - `TDescendantEntry<N extends TNode = TNode>`: Descendant entry from a node.
  - `TOperation<N extends TDescendant = TDescendant>`: operation types now accept unknown props.
*/

export const ELEMENT_HEADING_LARGE = 'h_lg';
export const ELEMENT_HEADING_MEDIUM = 'h_md';
export const ELEMENT_HEADING_SMALL = 'h_sm';
export const ELEMENT_PARAGRAPH = 'p';
export const ELEMENT_BLOCKQUOTE = 'blockquote';
export const ELEMENT_CALLOUT = 'callout';
export const ELEMENT_DIVIDER = 'hr';
export const ELEMENT_CODE_BLOCK = 'code_block';
export const ELEMENT_IMAGE = 'img';
export const ELEMENT_INLINE_IMAGE = 'inline_img';
export const ELEMENT_VIDEO_EMBED = 'video_embed';
export const ELEMENT_EMBED = 'embed';
export const ELEMENT_LINK = 'a';
export const ELEMENT_ANCHOR_LINK = 'anchor_link';
export const ELEMENT_INTERLINK = 'interlink';
export const ELEMENT_LI = 'li';
export const ELEMENT_HTML = 'html';
export const ELEMENT_TABLE = 'table';
export const ELEMENT_TH = 'th';
export const ELEMENT_TR = 'tr';
export const ELEMENT_TD = 'td';

/** List item, child */
export const ELEMENT_LIC = 'lic';
export const ELEMENT_OL = 'ol';
export const ELEMENT_UL = 'ul';

/** Marks */
export const MARK_BOLD = 'bold';
export const MARK_CODE = 'code';
export const MARK_ITALIC = 'italic';
export const MARK_STRIKETHROUGH = 'strikethrough';
export const MARK_UNDERLINE = 'underline';

/** Text */

export type EmptyText = {
  text: '';
};

export type PlainText = {
  text: string;
};

export type MyMark =
  | typeof MARK_BOLD
  | typeof MARK_ITALIC
  | typeof MARK_UNDERLINE
  | typeof MARK_STRIKETHROUGH
  | typeof MARK_CODE;

export type RichText = TText & {
  [mark in MyMark]?: true;
};

/** Inline Elements */

export interface TLinkElement extends TElement {
  url: string;
  target?: string;
}

export interface MyLinkElement extends TLinkElement {
  type: typeof ELEMENT_LINK;
  children: RichText[];
}

export interface MyAnchorLinkElement extends TElement {
  type: typeof ELEMENT_ANCHOR_LINK;
  linkId: string;
  children: RichText[];
}

export interface MyInterlinkElement extends TElement {
  type: typeof ELEMENT_INTERLINK;
  linkId: string;
  langId: string | undefined;
  linkTitle: string;
  children: RichText[];
}

export interface MyInlineImageElement extends TElement {
  type: typeof ELEMENT_INLINE_IMAGE;
  url: string;
  originalWidth?: number; // in px
  originalHeight?: number; // in px
  width?: number; // in px
  children: [EmptyText];
}

export type MyInlineElement =
  | MyLinkElement
  | MyInterlinkElement
  | MyAnchorLinkElement
  | MyInlineImageElement;

export type MyInlineDescendant = MyInlineElement | RichText;
export type MyInlineChildren = MyInlineDescendant[];

/** Block props */

// export interface MyIndentProps {
//   indent?: number;
// }

// export interface MyIndentListProps extends MyIndentProps {
//   listStart?: number;
//   listRestart?: number;
//   listStyleType?: string;
// }

export interface MyBlockElement extends TElement {
  id: string;
  updatedAt?: string;
  translatedAt?: string;
}

/** Blocks */

export interface MyParagraphElement extends MyBlockElement {
  type: typeof ELEMENT_PARAGRAPH;
  children: MyInlineChildren;
}

export interface MyHeadingLargeElement extends MyBlockElement {
  type: typeof ELEMENT_HEADING_LARGE;
  children: MyInlineChildren;
}

export interface MyHeadingMediumElement extends MyBlockElement {
  type: typeof ELEMENT_HEADING_MEDIUM;
  children: MyInlineChildren;
}

export interface MyHeadingSmallElement extends MyBlockElement {
  type: typeof ELEMENT_HEADING_SMALL;
  children: MyInlineChildren;
}

export interface MyBlockquoteElement extends MyBlockElement {
  type: typeof ELEMENT_BLOCKQUOTE;
  children: MyInlineChildren;
}

export interface MyCalloutElement extends MyBlockElement {
  type: typeof ELEMENT_CALLOUT;
  children: MyInlineChildren;
  variety?: 'success' | 'error' | 'info' | 'warning' | 'neutral';
}

export interface MyCodeBlockElement extends MyBlockElement {
  type: typeof ELEMENT_CODE_BLOCK;
  // children: MyCodeLineElement[];
  children: MyInlineChildren;
}

// export interface MyCodeLineElement extends TElement {
//   type: typeof ELEMENT_CODE_LINE;
//   children: PlainText[];
// }

/** Because type recursion breaks some of Plate types, this has */
export interface MyBulletedListElement extends TElement, MyBlockElement {
  type: typeof ELEMENT_UL;
  children: MyListItemElement[];
}

/** This is the actual type, but we can't use it in MyValues */
export interface MyBulletedListElementActual extends TElement, MyBlockElement {
  type: typeof ELEMENT_UL;
  children: MyListItemElementActual<MyBulletedListElementActual>[];
}

export interface MyNumberedListElement extends TElement, MyBlockElement {
  type: typeof ELEMENT_OL;
  children: MyListItemElement[];
}
export interface MyNumberedListElementActual extends TElement, MyBlockElement {
  type: typeof ELEMENT_OL;
  children: MyListItemElementActual<MyNumberedListElementActual>[];
}

export interface MyListItemElement extends TElement, MyBlockElement {
  type: typeof ELEMENT_LI;
  children: MyListItemChild[]; // | MyListElement but this causes an infinite loop error
}

/**
 * This isn't used by Plate but it uses type recursion, which models how ul + ol
 * blocks work
 */
export interface MyListItemElementActual<A extends TElement>
  extends TElement, MyBlockElement {
  type: typeof ELEMENT_LI;
  children: (MyListItemChild | A)[];
}

export interface MyListItemChild extends TElement, MyBlockElement {
  type: typeof ELEMENT_LIC;
  children: MyInlineChildren;
}

export interface MyImageElement extends MyBlockElement {
  type: typeof ELEMENT_IMAGE;
  url: string | null;
  linkUrl?: string;
  filename: string | null; // Generated filename
  caption?: RichText[];
  originalWidth?: number; // in px
  originalHeight?: number; // in px
  width?: number; // in px
  imgAlign: 'center' | 'end' | 'start' | 'stretch';
  children: [EmptyText];
}

export interface MyVideoEmbedElement extends MyBlockElement {
  type: typeof ELEMENT_VIDEO_EMBED;
  url: string | null;
  provider?: Providers | null;
  caption?: RichText[];
  width?: number; // in px
  align: 'center' | 'end' | 'start' | 'stretch';
  children: [EmptyText];
}

export interface MyEmbedElement extends MyBlockElement {
  type: typeof ELEMENT_EMBED;
  src: string | null;
  title?: string;
  height?: number; // in px
  children: [EmptyText];
}

export interface MyDividerElement extends MyBlockElement {
  type: typeof ELEMENT_DIVIDER;
  children: [EmptyText];
}

export interface MyHtmlElement extends MyBlockElement {
  type: typeof ELEMENT_HTML;
  useArticleStyles?: boolean;
  html?: string;
  css?: string;
  js?: string;
  children: [EmptyText];
}

export interface BorderStyle {
  // https://docx.js.org/api/enums/BorderStyle.html
  style?: string;
  size?: number;
  color?: string;
}

export interface MyTableElement extends MyBlockElement {
  type: typeof ELEMENT_TABLE;
  colSizes?: number[];
  marginLeft?: number;
  children: MyTableRowElement[];
}

export interface MyTableRowElement extends MyBlockElement {
  type: typeof ELEMENT_TR;
  size?: number;
  children: (MyTableCellElement | MyTableHeaderElement)[];
}

interface MyTableCellBase extends MyBlockElement {
  colSpan?: number;
  size?: number;
  children: MyParagraphElement[];
}

export interface MyTableCellElement extends MyTableCellBase {
  type: typeof ELEMENT_TD;
}

export interface MyTableHeaderElement extends MyTableCellBase {
  type: typeof ELEMENT_TH;
}

export type MyBlock = Exclude<MyElement, MyInlineElement>;
export type MyBlockEntry = TNodeEntry<MyBlock>;

export type MyRootBlock =
  | MyParagraphElement
  | MyHeadingLargeElement
  | MyHeadingMediumElement
  | MyHeadingSmallElement
  | MyBlockquoteElement
  | MyCodeBlockElement
  | MyDividerElement
  | MyHtmlElement
  | MyCalloutElement
  | MyBulletedListElement
  | MyNumberedListElement
  | MyImageElement
  | MyVideoEmbedElement
  | MyEmbedElement
  | MyTableElement;

export type MyValue = MyRootBlock[];

export type MyValueTypes = MyRootBlock['type'] | MyInlineElement['type'];

/** Editor types */

export type MyEditor = PlateEditor<MyValue>;
export type MyReactEditor = TReactEditor<MyValue>;
export type MyNode = ENode<MyValue>;
export type MyNodeEntry = ENodeEntry<MyValue>;
export type MyElement = EElement<MyValue>;
export type MyElementEntry = EElementEntry<MyValue>;
export type MyText = EText<MyValue>;
export type MyTextEntry = ETextEntry<MyValue>;
export type MyElementOrText = EElementOrText<MyValue>;
export type MyDescendant = EDescendant<MyValue>;

/** Plate types */

export type MyDecorate<P = PluginOptions> = Decorate<P, MyValue, MyEditor>;
export type MyDecorateEntry = DecorateEntry<MyValue>;
export type MyDOMHandler<P = PluginOptions> = DOMHandler<P, MyValue, MyEditor>;
export type MyInjectComponent = InjectComponent<MyValue>;
export type MyInjectProps = InjectProps<MyValue>;
export type MyKeyboardHandler<P = PluginOptions> = KeyboardHandler<
  P,
  MyValue,
  MyEditor
>;
export type MyOnChange<P = PluginOptions> = OnChange<P, MyValue, MyEditor>;
export type MyOverrideByKey = OverrideByKey<MyValue, MyEditor>;
export type MyPlatePlugin<P = PluginOptions> = PlatePlugin<
  P,
  MyValue,
  MyEditor
>;
export type MyPlatePluginInsertData = PlatePluginInsertData<MyValue>;
export type MyPlatePluginProps = PlatePluginProps<MyValue>;
export type MyPlateProps = PlateProps<MyValue, MyEditor>;
export type MySerializeHtml = SerializeHtml<MyValue>;
export type MyWithOverride<P = PluginOptions> = WithOverride<
  P,
  MyValue,
  MyEditor
>;
